import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { Boxes, LogIn, LogOut, ShoppingCart, Search, Info, Mail, Eye } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { useAuth } from '../contexts/AuthContext';
import { useCart } from '../contexts/CartContext';
import axios from 'axios';
import { getProductUnit, getPalletQuantity, canUseBancale } from '../utils/productUnits';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Prodotti = () => {
  const { isAuthenticated, user, logout, checkAuth } = useAuth();
  const { addToCart } = useCart();
  const location = useLocation();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedUnits, setSelectedUnits] = useState({});

  const handleUnitChange = (productId, unitType) => {
    setSelectedUnits(prev => ({ ...prev, [productId]: unitType }));
  };

  // Force auth check when returning from OAuth
  useEffect(() => {
    if (location.state?.user) {
      // User data was passed from AuthCallback, force refresh
      checkAuth();
    }
  }, [location.state, checkAuth]);

  useEffect(() => {
    fetchProducts();
  }, []);

  const fetchProducts = async () => {
    try {
      // Rimuovo withCredentials perché /api/products è pubblico
      const response = await axios.get(`${API}/products`);
      console.log('✅ Prodotti caricati:', response.data.length);
      console.log('✅ Primo prodotto:', response.data[0]);
      setProducts(response.data);
    } catch (error) {
      console.error('❌ Errore caricamento prodotti:', error);
      console.error('❌ Dettagli:', error.response?.data);
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleLogin = () => {
    const redirectUrl = window.location.origin + '/prodotti';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const handleRequestQuote = (product) => {
    const subject = `Richiesta Preventivo - ${product.name}`;
    const body = `Buongiorno,\n\nSono interessato al prodotto:\n\nProdotto: ${product.name}\nMarca: ${product.brand}\nCategoria: ${product.category}\n\nVorrei ricevere un preventivo.\n\nGrazie.`;
    window.location.href = `mailto:edilserviceduepuntozerosrl@gmail.com?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
  };

  // Filter products
  const filteredProducts = products.filter(product => {
    if (!searchTerm) return true;
    const searchLower = searchTerm.toLowerCase();
    return (
      (product.name && product.name.toLowerCase().includes(searchLower)) ||
      (product.brand && product.brand.toLowerCase().includes(searchLower)) ||
      (product.category && product.category.toLowerCase().includes(searchLower))
    );
  });

  // Debug logs
  console.log('📊 Totale prodotti:', products.length);
  console.log('📊 Prodotti filtrati:', filteredProducts.length);
  console.log('📊 Termini ricerca:', searchTerm);

  // Group by category
  const groupedProducts = filteredProducts.reduce((acc, product) => {
    const category = product.category || 'ALTRO';
    if (!acc[category]) {
      acc[category] = [];
    }
    acc[category].push(product);
    return acc;
  }, {});

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative h-[50vh] flex items-center overflow-hidden">
        <div className="absolute inset-0 z-0">
          <img 
            src="https://images.unsplash.com/photo-1637241612956-b7309005288b?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2OTV8MHwxfHNlYXJjaHwxfHxjb25zdHJ1Y3Rpb24lMjBtYXRlcmlhbHN8ZW58MHx8fHwxNzc0NTQyNjU0fDA&ixlib=rb-4.1.0&q=85"
            alt="Construction Materials"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-slate-900/90 to-slate-800/70"></div>
        </div>

        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-3xl">
            <h1 className="text-5xl md:text-6xl font-bold text-white mb-6">
              I Nostri Prodotti
            </h1>
            <p className="text-xl text-slate-300 leading-relaxed mb-8">
              Un'ampia gamma di materiali edili di alta qualità per ogni esigenza professionale
            </p>
          </div>
        </div>
      </section>

      {/* Avviso alla Clientela */}
      <section className="py-8 bg-blue-50 border-b border-blue-100">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="flex items-start gap-4 bg-white p-6 rounded-lg shadow-sm border border-blue-200">
              <Info className="text-blue-600 flex-shrink-0 mt-1" size={24} />
              <div>
                <h3 className="text-lg font-bold text-slate-800 mb-2">Avviso alla Clientela</h3>
                <p className="text-slate-700 leading-relaxed">
                  I prezzi dei prodotti sono riservati ai clienti registrati. 
                  Per visualizzare i prezzi, effettua il login con il tuo account Google. 
                  Se desideri richiedere un preventivo, utilizza il pulsante 
                  "Richiedi Preventivo" disponibile su ogni prodotto oppure aggiungi i prodotti al carrello 
                  dopo aver effettuato l'accesso.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Auth Status & Search */}
      <section className="py-8 bg-slate-50">
        <div className="container mx-auto px-4">
          <div className="max-w-2xl mx-auto">
            {!isAuthenticated ? (
              <div className="bg-orange-100 border border-orange-300 rounded-lg p-4 mb-6 text-center">
                <p className="text-orange-800 mb-3">
                  <LogIn className="inline mr-2" size={18} />
                  <strong>Accedi</strong> per visualizzare i prezzi e aggiungere prodotti al carrello
                </p>
                <Button
                  onClick={handleGoogleLogin}
                  size="sm"
                  className="bg-orange-600 hover:bg-orange-700"
                >
                  Accedi con Google
                </Button>
              </div>
            ) : (
              <div className="flex items-center gap-4 bg-green-100 border border-green-300 rounded-lg p-4 mb-6">
                <div className="flex-1">
                  <p className="text-green-800">
                    ✅ Connesso come <span className="font-semibold">{user?.email}</span>
                  </p>
                </div>
                <Button
                  onClick={() => {
                    logout();
                    window.location.href = '/';
                  }}
                  size="sm"
                  variant="outline"
                  className="border-green-600 text-green-700 hover:bg-green-600 hover:text-white"
                >
                  <LogOut className="mr-2" size={16} />
                  Esci
                </Button>
              </div>
            )}

            {/* Search Bar */}
            <div className="relative">
              <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 text-slate-400" size={20} />
              <Input
                type="text"
                placeholder="Cerca prodotti, marchi o categorie..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-12 py-6 text-lg"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Products Grid */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          {loading ? (
            <div className="text-center py-20">
              <div className="inline-block animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-orange-600"></div>
              <p className="mt-4 text-slate-600">Caricamento prodotti...</p>
            </div>
          ) : filteredProducts.length === 0 ? (
            <div className="text-center py-20">
              <Boxes size={64} className="mx-auto text-slate-300 mb-4" />
              <h3 className="text-2xl font-bold text-slate-800 mb-2">
                Nessun prodotto trovato
              </h3>
              <p className="text-slate-600">
                {searchTerm ? 'Prova a cercare con un termine diverso' : 'Nessun prodotto disponibile al momento'}
              </p>
            </div>
          ) : (
            <div className="space-y-16">
              {Object.entries(groupedProducts).map(([category, categoryProducts]) => (
                <div key={category}>
                  <h2 className="text-3xl font-bold text-slate-800 mb-8 pb-4 border-b-2 border-orange-600">
                    {category}
                  </h2>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                    {categoryProducts.map((product) => {
                      const unit = getProductUnit(product);
                      const palletQty = getPalletQuantity(product);
                      const showBancale = canUseBancale(product);
                      const selectedUnit = selectedUnits[product.product_id] || unit;

                      return (
                        <Card key={product.product_id} className="overflow-hidden hover:shadow-xl transition-shadow">
                          <CardContent className="p-0">
                            {/* Product Image */}
                            <div className="relative bg-white h-48 flex items-center justify-center overflow-hidden border-b border-slate-100">
                              {product.image_url ? (
                                <img 
                                  src={product.image_url} 
                                  alt={product.name}
                                  className="w-full h-full object-contain p-4"
                                  onError={(e) => {
                                    e.target.style.display = 'none';
                                    e.target.parentElement.querySelector('.fallback-icon').style.display = 'flex';
                                  }}
                                />
                              ) : null}
                              <div className={`fallback-icon absolute inset-0 flex items-center justify-center ${product.image_url ? 'hidden' : ''}`}>
                                <Boxes className="text-slate-300" size={64} />
                              </div>
                              
                              {product.availability_status === 'Non disponibile' && (
                                <div className="absolute inset-0 bg-slate-900/70 flex items-center justify-center">
                                  <span className="text-white font-bold text-lg">NON DISPONIBILE</span>
                                </div>
                              )}
                              
                              <div className="absolute top-3 right-3">
                                <span className="bg-white px-3 py-1 rounded-full text-xs font-bold text-slate-700 shadow-md" translate="no">
                                  {product.brand}
                                </span>
                              </div>
                              {product.discount_percentage > 0 && (
                                <div className="absolute top-3 left-3">
                                  <span className="bg-red-600 text-white px-3 py-1 rounded-full text-xs font-bold shadow-md">
                                    -{product.discount_percentage}%
                                  </span>
                                </div>
                              )}
                            </div>

                            <div className="p-6">
                              <div className="flex items-start justify-between gap-2 mb-2">
                                <h3 className="text-xl font-bold text-slate-800 line-clamp-2 flex-1" translate="no">
                                  {product.name}
                                </h3>
                                
                                {/* Pulsante Vedi Dettagli */}
                                {product.description && (
                                  <Dialog>
                                    <DialogTrigger asChild>
                                      <Button variant="ghost" size="sm" className="text-blue-600 hover:text-blue-700 hover:bg-blue-50">
                                        <Eye size={18} className="mr-1" />
                                        Dettagli
                                      </Button>
                                    </DialogTrigger>
                                    <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
                                      <DialogHeader>
                                        <DialogTitle className="text-2xl font-bold text-slate-800 mb-4">
                                          {product.name}
                                        </DialogTitle>
                                      </DialogHeader>
                                      <div className="space-y-4">
                                        {/* Product Image in Modal */}
                                        {product.image_url && (
                                          <div className="bg-white rounded-lg border p-4 flex items-center justify-center h-64">
                                            <img 
                                              src={product.image_url} 
                                              alt={product.name}
                                              className="max-h-full max-w-full object-contain"
                                            />
                                          </div>
                                        )}
                                        
                                        {/* Product Info */}
                                        <div className="grid grid-cols-2 gap-4 text-sm">
                                          <div>
                                            <span className="font-semibold text-slate-700">Marchio:</span>
                                            <p className="text-slate-600">{product.brand}</p>
                                          </div>
                                          <div>
                                            <span className="font-semibold text-slate-700">Categoria:</span>
                                            <p className="text-slate-600">{product.category}</p>
                                          </div>
                                          <div>
                                            <span className="font-semibold text-slate-700">Disponibilità:</span>
                                            <p className={`font-semibold ${
                                              product.availability_status === 'Disponibile in Magazzino' ? 'text-green-600' :
                                              product.availability_status === 'Su Ordinazione' ? 'text-orange-600' :
                                              'text-red-600'
                                            }`}>
                                              {product.availability_status}
                                            </p>
                                          </div>
                                          {isAuthenticated && product.show_price && (
                                            <div>
                                              <span className="font-semibold text-slate-700">Prezzo:</span>
                                              <p className="text-orange-600 font-bold text-lg">
                                                €{(product.price * (1 - (product.discount_percentage || 0) / 100)).toFixed(2)}
                                              </p>
                                            </div>
                                          )}
                                        </div>
                                        
                                        {/* Description */}
                                        <div className="border-t pt-4">
                                          <h4 className="font-semibold text-slate-800 mb-2 text-lg">Descrizione</h4>
                                          <p className="text-slate-700 leading-relaxed whitespace-pre-wrap">
                                            {product.description}
                                          </p>
                                        </div>
                                      </div>
                                    </DialogContent>
                                  </Dialog>
                                )}
                              </div>
                              
                              {product.description && (
                                <p className="text-slate-600 text-sm mb-4 line-clamp-2">
                                  {product.description}
                                </p>
                              )}

                              {/* Availability Status */}
                              <div className="flex items-center justify-between mb-3">
                                <span className="text-sm text-slate-600">Disponibilità:</span>
                                <span className={`text-sm font-semibold px-3 py-1 rounded-full ${
                                  product.availability_status === 'Disponibile in Magazzino' 
                                    ? 'bg-green-100 text-green-700' 
                                    : product.availability_status === 'Disponibile su Ordinazione'
                                    ? 'bg-blue-100 text-blue-700'
                                    : 'bg-red-100 text-red-700'
                                }`}>
                                  {product.availability_status || 'Disponibile in Magazzino'}
                                </span>
                              </div>

                              {/* Price Section - Only for authenticated users */}
                              {isAuthenticated && product.show_price ? (
                                <div className="mb-4">
                                  <div className="flex items-baseline gap-2 mb-2">
                                    {product.discount_percentage > 0 && (
                                      <span className="text-slate-400 line-through text-lg">
                                        €{(product.price / (1 - product.discount_percentage / 100)).toFixed(2)}
                                      </span>
                                    )}
                                    <span className="text-3xl font-bold text-orange-600">
                                      €{product.price?.toFixed(2) || '0.00'}
                                    </span>
                                    <span className="text-sm text-slate-600">/{unit}</span>
                                  </div>
                                  
                                  {/* Unit Selection for Bancale */}
                                  {showBancale && (
                                    <div className="mb-3 space-y-2">
                                      <div className="flex gap-2">
                                        <button
                                          onClick={() => handleUnitChange(product.product_id, unit)}
                                          className={`flex-1 py-2 px-3 rounded border-2 text-sm font-semibold transition-all ${
                                            selectedUnit === unit
                                              ? 'border-orange-600 bg-orange-50 text-orange-700'
                                              : 'border-slate-200 text-slate-600 hover:border-slate-300'
                                          }`}
                                        >
                                          {unit.charAt(0).toUpperCase() + unit.slice(1)}
                                        </button>
                                        <button
                                          onClick={() => handleUnitChange(product.product_id, 'bancali')}
                                          className={`flex-1 py-2 px-3 rounded border-2 text-sm font-semibold transition-all ${
                                            selectedUnit === 'bancali'
                                              ? 'border-orange-600 bg-orange-50 text-orange-700'
                                              : 'border-slate-200 text-slate-600 hover:border-slate-300'
                                          }`}
                                        >
                                          A Bancale ({palletQty})
                                        </button>
                                      </div>
                                      {selectedUnit === 'bancali' && (
                                        <div className="text-sm text-slate-600 bg-blue-50 p-2 rounded">
                                          💡 1 Bancale = {palletQty} {unit} | Prezzo: €{(product.price * palletQty).toFixed(2)}
                                        </div>
                                      )}
                                    </div>
                                  )}
                                  
                                  {/* Add to Cart Button */}
                                  {product.availability_status !== 'Non disponibile' && (
                                    <Button
                                      onClick={() => addToCart(product, 1, selectedUnit)}
                                      className="w-full bg-gradient-to-r from-orange-600 to-orange-500 hover:from-orange-700 hover:to-orange-600 text-white"
                                    >
                                      <ShoppingCart className="mr-2" size={18} />
                                      Aggiungi al Carrello
                                    </Button>
                                  )}
                                </div>
                              ) : !isAuthenticated ? (
                                /* Request Quote Button for non-authenticated users */
                                <div className="mb-4">
                                  <div className="bg-slate-100 p-4 rounded-lg mb-3 text-center">
                                    <p className="text-slate-600 font-semibold">
                                      🔒 Prezzo riservato ai clienti registrati
                                    </p>
                                  </div>
                                  <Button
                                    onClick={() => handleRequestQuote(product)}
                                    variant="outline"
                                    className="w-full border-2 border-blue-600 text-blue-600 hover:bg-blue-600 hover:text-white mb-2"
                                  >
                                    <Mail className="mr-2" size={18} />
                                    Richiedi Preventivo
                                  </Button>
                                  <Button
                                    onClick={handleGoogleLogin}
                                    className="w-full bg-orange-600 hover:bg-orange-700"
                                  >
                                    <LogIn className="mr-2" size={18} />
                                    Accedi per vedere il prezzo
                                  </Button>
                                </div>
                              ) : (
                                /* "Contattaci per il prezzo" */
                                <div className="mb-4">
                                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 text-center">
                                    <p className="text-blue-800 font-semibold mb-2">
                                      📞 Contattaci per il prezzo
                                    </p>
                                    <Button
                                      onClick={() => handleRequestQuote(product)}
                                      variant="outline"
                                      className="border-blue-600 text-blue-600 hover:bg-blue-600 hover:text-white"
                                    >
                                      <Mail className="mr-2" size={18} />
                                      Richiedi Preventivo
                                    </Button>
                                  </div>
                                </div>
                              )}
                            </div>
                          </CardContent>
                        </Card>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>
    </div>
  );
};

export default Prodotti;
